import type { LensChartModel } from './lensChart';

/** Readable numeric ticks shared by all renderers. */
export const AXIS_INTERVALS = 4;

/** Prefer familiar steps over fitting the peak to an exact number of intervals. */
const STEP_FACTORS = [1, 2, 2.5, 5, 10];

export type AxisBound = {
  /** Absent means the axis starts at zero, which is the default for every card. */
  min?: number;
  max?: number;
  interval?: number;
  /** Every tick from min to max, for renderers that take explicit tick values. */
  values?: Array<number>;
};

/**
 * A floor and a ceiling an author set by hand, either of which may be absent.
 *
 * A floor above zero is a truncated axis, which is a real request and a real
 * way to mislead: on a series moving between 4,100 and 4,300 a zero-based axis
 * shows a flat line, and a 4,000 floor shows the movement that is actually
 * there. Both drawings are honest about the numbers and only one of them is
 * readable, so the control exists. What it is not is a default.
 */
export type AxisOverride = {
  min?: number;
  max?: number;
};

export function niceAxisBound(peak: number, intervals: number = AXIS_INTERVALS, floor = 0): AxisBound {
  const span = peak - floor;
  if (!Number.isFinite(span) || span <= 0) {
    return {};
  }
  const rough = span / intervals;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = STEP_FACTORS.map((factor) => factor * magnitude).find((candidate) => candidate >= rough);
  if (!step) {
    return {};
  }
  return {
    ...(floor === 0 ? {} : { min: floor }),
    max: floor + step * Math.ceil(span / step),
    interval: step,
    values: Array.from({ length: Math.ceil(span / step) + 1 }, (_, index) => floor + step * index),
  };
}

/** The largest value an axis has to fit: a stack's total, or its tallest single mark. */
export function axisPeak(model: LensChartModel, names: Array<string>, stacked: boolean): number {
  if (!names.length) {
    return 0;
  }
  return model.rows.reduce((peak, row) => {
    const values = names.map((name) => (typeof row[name] === 'number' ? (row[name] as number) : Number(row[name])));
    const finite = values.filter((value) => Number.isFinite(value));
    if (!finite.length) {
      return peak;
    }
    const height = stacked ? finite.reduce((sum, value) => sum + value, 0) : Math.max(...finite);
    return Math.max(peak, height);
  }, 0);
}

/** Negative values need a zero-crossing axis, so those are left to the renderer. */
export function hasNegative(model: LensChartModel, names: Array<string>): boolean {
  return model.rows.some((row) => names.some((name) => Number(row[name]) < 0));
}

/**
 * The lowest value any single mark has to reach.
 *
 * Per point rather than per stack, because a floor is checked against what gets
 * drawn: on a grouped card the shortest bar is one series' own value, and the
 * stack total is nobody's mark.
 */
export function axisFloor(model: LensChartModel, names: Array<string>): number {
  const values = model.rows.flatMap((row) =>
    names.map((name) => (typeof row[name] === 'number' ? (row[name] as number) : Number(row[name]))),
  );
  const finite = values.filter((value) => Number.isFinite(value));
  return finite.length ? Math.min(...finite) : 0;
}

/**
 * Whether an axis was drawn somewhere other than zero.
 *
 * Read off the bound the renderer is about to use, never off the config. A floor
 * the rules set aside leaves no `min` behind, so a card that fell back to
 * zero-based does not carry a caption saying it did not.
 */
export function truncatesZero(bound: AxisBound): boolean {
  return (bound.min !== undefined && bound.min > 0) || (bound.max !== undefined && bound.max < 0);
}

/**
 * The lowest and highest value a card actually plots, across every series.
 *
 * The card editor has no rows and cannot draw the card it is editing, so this is
 * what lets it name the numbers an axis floor has to clear instead of leaving
 * someone to find out after saving.
 */
export function plottedValueExtent(model: LensChartModel): { min: number; max: number } | undefined {
  const values = model.rows.flatMap((row) =>
    model.series.map((name) => (typeof row[name] === 'number' ? (row[name] as number) : Number(row[name]))),
  );
  const finite = values.filter((value) => Number.isFinite(value));
  return finite.length ? { min: Math.min(...finite), max: Math.max(...finite) } : undefined;
}

/**
 * The bound for a set of series, or an empty bound when the renderer should decide.
 *
 * An override wins over both the zero floor and the derived ceiling, and it wins
 * over the negative-values bail-out too: an author who has typed a floor has
 * said where the axis starts, and that is exactly the case a zero-crossing
 * default is wrong for.
 *
 * One rule sets a floor aside: any plotted value below it. Both renderers clip
 * a mark to the plot area, so a 3,900 bar on a 4,000 floor is not drawn short,
 * it is absent, and an absent bar reads as zero. Refusing is discrete and can be
 * explained; silently sliding the floor down to fit would mean the number the
 * author typed is not the number that draws. The dialog blocks this at save
 * time, so it is reached only when later data dips under a floor that was valid
 * when it was set, and then the card reverts to zero-based with every mark
 * visible.
 *
 * A stack never takes a floor at all. Cutting the bottom segment misstates every
 * proportion above it, and the rule lives here so both renderers agree.
 */
export function boundFor(
  model: LensChartModel,
  names: Array<string>,
  stacked: boolean,
  override?: AxisOverride,
): AxisBound {
  const peak = axisPeak(model, names, stacked);
  const ceiling = override?.max;
  const floor = stacked ? undefined : override?.min;
  const usableFloor =
    floor !== undefined && Number.isFinite(floor) && floor < (ceiling ?? peak) && floor <= axisFloor(model, names)
      ? floor
      : undefined;

  if (usableFloor === undefined && ceiling === undefined) {
    return hasNegative(model, names) ? {} : niceAxisBound(peak);
  }

  // A hand-set ceiling is the axis, not a starting point for a tick ladder that
  // would round it somewhere else, so it is returned as given and the interval
  // is left to the renderer.
  if (ceiling !== undefined) {
    return {
      ...(usableFloor === undefined ? {} : { min: usableFloor }),
      max: ceiling,
    };
  }

  const bound = niceAxisBound(peak, AXIS_INTERVALS, usableFloor);
  // usableFloor already sits below the peak, so the ladder only comes back empty
  // for a degenerate peak. Zero-based is the safe drawing in that case.
  return bound.max === undefined ? niceAxisBound(peak) : bound;
}
